import { yupResolver } from "@hookform/resolvers/yup";
import {
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import * as yup from "yup";
import BookingInfoCard from "@/components/booking/bookingInfoCard";
import PaymentCardFormSection from "@/components/booking/payment/paymentCardFormSection";
import { useClientBookingContext } from "@/contexts/clientBookingContext";
import useBookingClientDetails from "@/hooks/booking/useBookingClientDetails";
import useCreditCardInputs from "@/hooks/payments/useCreditCardInputs";

const schema = yup.object().shape({
  zipCode: yup
    .string()
    .length(5, "Please provide valid zip code")
    .required("Enter your zip code."),
  consentToCharge: yup.boolean().isTrue("Please agree to the terms."),
  ageConsent: yup
    .boolean()
    .isTrue(
      "Confirm your age and consent to the Privacy and Telehealth terms."
    ),
});

export type CollectCreditCardFormInput = {
  zipCode: string;
  consentToCharge: boolean;
  ageConsent: boolean;
  marketingConsent: boolean;
};

export default function CollectCreditCard() {
  const {
    clientSecret,
    clientDetails,
    creditCardVerified,
    depositsEnabled,
    handleBookingSubmit,
    setCreditCardVerified,
  } = useClientBookingContext();

  const { getClientSecret } = useBookingClientDetails();

  const {
    paymentCardValidationInfo,
    isPaymentCardInfoValid,
    showPaymentCardEmptyFieldErrors,
    setShowPaymentCardEmptyFieldErrors,
    onElementChange,
  } = useCreditCardInputs();

  const methods = useForm<CollectCreditCardFormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      consentToCharge: false,
      ageConsent: false,
      marketingConsent: false,
      zipCode: "",
    },
  });

  const stripe = useStripe();
  const elements = useElements();

  const setupCard = async () => {
    if (creditCardVerified) {
      return await handleBookingSubmit();
    }

    if (!stripe || !elements) return;
    if (!isPaymentCardInfoValid) {
      setShowPaymentCardEmptyFieldErrors(true);
      return;
    }

    // If there was a failed deposit payment, we need to get a new client secret
    const currentClientSecret =
      clientSecret ?? (await getClientSecret(clientDetails));

    const { error } = await stripe.confirmCardSetup(currentClientSecret, {
      payment_method: {
        card: elements.getElement(CardNumberElement),
        billing_details: {
          address: {
            postal_code: methods.getValues().zipCode,
          },
        },
      },
    });

    if (error) {
      console.error(error);
      toast.error(error.message);
      return;
    }

    setCreditCardVerified(true);
    await handleBookingSubmit();
  };

  useEffect(() => {
    // In case the user goes back to the previous step and then comes back to this step, they may want to enter different card details
    return () => {
      setCreditCardVerified(false);
    };
  }, [setCreditCardVerified]);

  const buttonCopy = depositsEnabled
    ? "Pay deposit and book appointment"
    : "Book appointment";

  return (
    <BookingInfoCard>
      <FormProvider {...methods}>
        <PaymentCardFormSection
          setupCard={async () => {
            await setupCard();
          }}
          paymentCardValidationInfo={paymentCardValidationInfo}
          showPaymentCardEmptyFieldErrors={showPaymentCardEmptyFieldErrors}
          onElementChange={onElementChange}
          buttonTitle={buttonCopy}
        />
      </FormProvider>
    </BookingInfoCard>
  );
}
